import '@trp-beacon-ui-kit/foundation/style-tokens/css/variables.css';
import '@trp-beacon-ui-kit/foundation/fonts/fonts.css';
import { Box } from '@trp-individual-investor-adv-svc/ii-prosp-components';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import sortBy from 'lodash.sortby';
import api from '../actions/EndPoints';
import axiosInstance from '../actions/AxiosInstance';
import { dispatchOverlayLoad } from './shared/Analytics';
import {
    setLastRequestTime,
    engagementType,
    getIsRasHH,
    log,
    showModal,
    scrollTo,
    scrollToTop,
    closeModal,
} from '../Utils';
import { adviceProspectusBaseUrl } from './shared/Links';
import InvInfoPanel from '../components/ConsentComponents/InvInfoPanel';
import { PsBeaconButton } from './shared/BeaconRegister';
import ExpansionPanelConsent from './shared/ExpansionPanelConsent';
import HubPageBase from './HubPages/HubPageBase';
import ProspectusPanel from './ConsentComponents/ProspectusPanel';
import SelectionError from './shared/Errors/SelectionError';
import LoadingSpinner from '../containers/LoadingSpinner';
import {
    acctSelHeader,
    adviceFundsList,
    InvestmentInfoContent,
} from './ConsentComponents/ConsentContent';
import ClientAgrPanel from './ConsentComponents/ClientAgrPanel';
import { GENERAL_HUB_ERROR_MODAL, HUB_GENERAL_ERROR_OVERLAY } from '../Constants';

const getOptions = (apiKey) => ({
    headers: {
        Accept: 'application/json',
        'X-TRP-Requested-with': 'XMLHttpRequest',
        'Content-Type': 'application/json',
        apiKey: `${apiKey}`,
    },
});

const getFundListData = (options, fundsList, setADVFundsListFetched, setADVFundsList) => {
    axios
        .post(
            api.prospectusesAPI,
            {
                query: 'query MultipleMutualFundQuery($tickers: [String]!) {mutualFunds(tickers: $tickers){ name ticker cusip trpCode }}',

                variables: {
                    tickers: fundsList,
                },
            },
            options
        )
        .then((response) => {
            const adviceFundsListData = response.data ? response.data.data.mutualFunds : [];
            const sortAdvFundsList = sortBy(adviceFundsListData, (o) => o.name);

            const advFundsList = sortAdvFundsList.map((obj) => ({
                ...obj,
                advProspectusURL: adviceProspectusBaseUrl + obj.cusip,
                displayName: obj.name.replace('T. Rowe Price ', ''),
            }));
            setADVFundsListFetched(true);
            setADVFundsList(advFundsList);
        })
        .catch((error) => {
            const showErrorModalPromise = Promise.resolve(showModal(GENERAL_HUB_ERROR_MODAL));
            showErrorModalPromise
                .then(() => console.log(error))
                .then(() => dispatchOverlayLoad(HUB_GENERAL_ERROR_OVERLAY))
                .then(() => setLastRequestTime());
        })
        .then(() => setLastRequestTime());
};

const AccountSelection = ({ state, dispatch, profile }) => {
    const { numberOfClients, apiKey, consentPreselect } = state;
    const isRasHH = getIsRasHH(state.planningGroup, state.engagementStatus);
    const showYodleeConsent = state.isRegisteredUser && state.isPrimary;
    const getEngagementType = engagementType(isRasHH, state.isPrimary);
    const prospectusesControl = document.querySelectorAll(`[id="checkedProspectuses-control"]`)[0];
    const confirmationControl = document.querySelectorAll(`[id="review-checkbox-control"]`)[0];
    const advControl = document.querySelectorAll(`[id="client-agreement-control"]`)[0];

    const prospectusesCheckbox = document.querySelectorAll(`[id="checkedProspectuses"]`)[0];
    const confirmationCheckbox = document.querySelectorAll(`[id="checkedConfirmation"]`)[0];
    const advCheckbox = document.querySelectorAll(`[id="agreementAdv"]`)[0];
    const [buttonSize, setButtonSize] = useState(window.innerWidth <= 600 ? '100%' : 'inherit');
    const smallScreenSize = window.matchMedia('(max-width: 600px)');
    const prospCheckboxLabel = `I confirm the above authorization settings, and I received the Prospectuses for the Service Funds and
        the Retirement Advisory Service Form ADV, including Part 2B Brochure Supplements.`;

    smallScreenSize.addEventListener('change', function () {
        setButtonSize(smallScreenSize.matches ? '100%' : 'inherit');
    });

    const [checkError, setCheckError] = useState({
        checkedAdv: false,
        checkedProspectuses: false,
        checkedConfirmation: false,
    });

    const [notificationContent, setNotificationContent] = useState([]);
    const [consentInProgress, setConsentInProgress] = useState(false);
    const [agreeComplete, setAgreeComplete] = useState(false);
    const [ADVFundsList, setADVFundsList] = useState([]);
    const [ADVFundsListFetched, setADVFundsListFetched] = useState(false);
    const [checkState, setCheckState] = useState({
        checkedProspectuses: false,
        checkedTrust: consentPreselect?.consentTrust,
        checked401k: consentPreselect?.consent401k,
        checkedYodlee: consentPreselect?.consentYodlee,
        checkedAdv: false,
        checkedConfirmation: false,
    });

    const handleCheckboxErrors = () => {
        const getScrollId = () => {
            if (!checkState.checkedProspectuses) {
                return 'prospectuses-container-header';
            }
            if (!checkState.checkedAdv) {
                return 'client-agreement-container-header';
            }
            if (!checkState.checkedConfirmation) {
                return 'inv-info-container-header';
            }
            return '';
        };

        setCheckError({
            ...checkError,
            checkedProspectuses: !checkState.checkedProspectuses,
            checkedConfirmation: !checkState.checkedConfirmation,
            checkedAdv: !checkState.checkedAdv,
        });
        if (!checkState.checkedProspectuses) {
            prospectusesControl?.setAttribute('invalid', '');
            prospectusesCheckbox?.checkValidity();
        }

        if (!checkState.checkedConfirmation) {
            confirmationControl?.setAttribute('invalid', '');
            confirmationCheckbox?.checkValidity();
        }

        if (!checkState.checkedAdv) {
            advControl?.setAttribute('invalid', '');
            advCheckbox?.checkValidity();
        }

        scrollTo(getScrollId());
    };

    useEffect(() => {
        getFundListData(
            getOptions(apiKey),
            adviceFundsList,
            setADVFundsListFetched,
            setADVFundsList
        );
    }, [apiKey, setADVFundsListFetched, setADVFundsList]);

    const handleChange = (name) => (event) => {
        log(console.log(name, checkState));
        setCheckState({ ...checkState, [name]: event.target.checked });
    };

    useEffect(() => {
        scrollToTop();
    }, []);

    useEffect(
        () => () => {
            const keysToRemove = ['emailInv', 'invitePinNumber', 'joiningRasHH'];
            keysToRemove.forEach((k) => localStorage.removeItem(k));
            keysToRemove.forEach((k) => sessionStorage.removeItem(k));
            log(console.log('encodedUrlText is removed', sessionStorage));
        },
        [agreeComplete]
    );

    useEffect(() => {
        if (checkState?.checkedProspectuses) {
            Promise.resolve(prospectusesControl.removeAttribute('invalid'))
                .then(() => prospectusesCheckbox.removeAttribute('invalid'))
                .then(() => prospectusesCheckbox.removeAttribute('aria-invalid'))
                .then(() =>
                    setCheckError((prevState) => ({ ...prevState, checkedProspectuses: false }))
                );
        }
        if (checkState?.checkedConfirmation) {
            Promise.resolve(confirmationControl.removeAttribute('invalid'))
                .then(() => confirmationCheckbox.removeAttribute('invalid'))
                .then(() => confirmationCheckbox.removeAttribute('aria-invalid'))
                .then(() =>
                    setCheckError((prevState) => ({ ...prevState, checkedConfirmation: false }))
                );
        }

        if (checkState?.checkedAdv) {
            Promise.resolve(advControl.removeAttribute('invalid'))
                .then(() => advCheckbox.removeAttribute('invalid'))
                .then(() => advCheckbox.removeAttribute('aria-invalid'))
                .then(() => setCheckError((prevState) => ({ ...prevState, checkedAdv: false })));
        }
    }, [checkState]);

    useEffect(() => {
        const prospectusesErr = {
            section: 'I',
            content: prospCheckboxLabel,
            error: checkError.checkedProspectuses,
        };
        const confirmationError = {
            section: 'III',
            content: InvestmentInfoContent.checkboxLabel,
            error: checkError.checkedConfirmation,
        };

        const advErr = {
            section: 'II',
            content: 'I agree',
            error: checkError.checkedAdv,
        };

        setNotificationContent([prospectusesErr, advErr, confirmationError]);
    }, [checkError]);

    const handleDispatch = (e) => {
        e?.preventDefault();
        if (
            checkState.checkedProspectuses &&
            checkState.checkedConfirmation &&
            checkState.checkedAdv
        ) {
            setConsentInProgress(true);
        }

        const headerPayload = {
            consent401k: checkState.checked401k,
            consentYodleeIndicator: checkState.checkedYodlee,
            consentTrust: checkState.checkedTrust,
            consentEDelivery: null,
            engagementType: getEngagementType,
        };

        const coClientHeaderPayload = {
            clientConsentRequest: { ...headerPayload },
            coClientVerificationRequest: {
                encodedUrlText: JSON.parse(sessionStorage.getItem('emailInv'))?.encodedUrlText,
                invitePinNumber: sessionStorage.getItem('invitePinNumber'),
            },
        };

        async function updateConsent(_key_) {
            const switchApiCall =
                profile.type === 'CLIENT_CONSENTED_RAS_ACTIVE_RAS_HH_CO_CLIENT_JOIN'
                    ? api.coClientConsentAPI
                    : api.consentAPI;
            const getHeaderPayload =
                profile.type === 'CLIENT_CONSENTED_RAS_ACTIVE_RAS_HH_CO_CLIENT_JOIN'
                    ? coClientHeaderPayload
                    : headerPayload;

            await axiosInstance
                .post(switchApiCall, getHeaderPayload, {
                    headers: {
                        apikey: `${_key_}`,
                    },
                })
                .then((res) => {
                    if (res) {
                        setLastRequestTime();
                        dispatch({
                            type: 'CONSENT_UPDATED',
                            isConsented: true,
                        });

                        setAgreeComplete(true);
                        if (numberOfClients > 1) {
                            setConsentInProgress(false);
                            window.location.replace('/usis/advice/fps/profile');
                        } else {
                            setAgreeComplete(true);
                            setConsentInProgress(false);
                            setConsentInProgress(false);
                            window.location.replace('/usis/advice/fps/profile');
                        }
                    }
                })
                .catch((error) => {
                    const showErrorModalPromise = Promise.resolve(
                        showModal(GENERAL_HUB_ERROR_MODAL)
                    );
                    showErrorModalPromise
                        .then(() => console.log(error))
                        .then(() => closeModal('acct-selection'))
                        .then(() => dispatchOverlayLoad(HUB_GENERAL_ERROR_OVERLAY))
                        .then(() => setConsentInProgress(false))
                        .then(() => setLastRequestTime())
                        .then(() => setAgreeComplete(true));
                });
        }
        if (
            checkState.checkedProspectuses &&
            checkState.checkedConfirmation &&
            checkState.checkedAdv &&
            !consentInProgress
        ) {
            updateConsent(apiKey);
        } else {
            handleCheckboxErrors();
        }
    };

    return (
        <HubPageBase state={state} dispatch={dispatch} keepInv noInvModal>
            <Box
                className="b-container-content"
                sx={{ inlineSize: 'unset !important', paddingTop: '40px' }}>
                {(checkError.checkedConfirmation || checkError?.checkedProspectuses) && (
                    <Box sx={{ paddingBottom: '48px' }}>
                        <SelectionError errors={notificationContent} />
                    </Box>
                )}
                <Box id="ras-auth-card">
                    <Box id="ras-auth-full-content-container">
                        <Box id="ras-auth-title-content-container">
                            <ExpansionPanelConsent>
                                <ProspectusPanel
                                    open
                                    panelNum="I"
                                    headerText="Prospectuses for funds that can be recommended through the Service"
                                    ADVFundsList={ADVFundsList}
                                    listLoaded={ADVFundsListFetched}
                                    checkError={checkError}
                                    state={state}
                                    showCheckbox
                                    handleChange={handleChange}
                                    prospectusesDisplay
                                    includeHeader
                                />
                                <ClientAgrPanel
                                    panelNum="II"
                                    checkBox
                                    open
                                    handleChange={handleChange}
                                    checkError={checkError}
                                    checkState={checkState}
                                />
                                <InvInfoPanel
                                    open
                                    headerText={acctSelHeader}
                                    panelNum="III"
                                    handleChange={handleChange}
                                    checkError={checkError}
                                    checkState={checkState}
                                    showYodleeConsent={showYodleeConsent}>
                                    <Box id="button-container" sx={{ paddingTop: '40px' }}>
                                        <PsBeaconButton
                                            onClick={(e) => !consentInProgress && handleDispatch(e)}
                                            scale="jumbo"
                                            style={{ whiteSpace: 'nowrap', width: buttonSize }}
                                            state={agreeComplete ? 'success' : ''}>
                                            {!consentInProgress && 'Continue'}
                                            {consentInProgress && (
                                                <LoadingSpinner scale="mini" text="Loading..." />
                                            )}
                                        </PsBeaconButton>
                                    </Box>
                                </InvInfoPanel>
                            </ExpansionPanelConsent>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </HubPageBase>
    );
};

export default AccountSelection;
